import { createApi, fakeBaseQuery } from '@reduxjs/toolkit/query/react';
import type { RecordResult, RecordStructure, RecordsWrapper } from '../../@types/records';
import {
  addRecord,
  deleteRecord,
  getOfflineQueryData,
  getRecord,
  getRecordsByFormId,
  updateRecord,
} from '../../db/odin-db-client';
import type { OdinDBRecordMutation } from '../../db/odin-db-types';
import { isDbRecordId } from '../../db/odin-db-util';
import { createRecordData } from '../../utils/data-transformation-utils';
import type { SyncDetails } from '../middleware/sync-transforms';

export interface DbRecord {
  id?: number;
  record: RecordResult;
  syncDetails?: SyncDetails;
}

export const dbApi = createApi({
  reducerPath: 'dbApi',
  keepUnusedDataFor: 0,
  baseQuery: fakeBaseQuery(),
  tagTypes: ['OfflineRecord'],
  endpoints: (builder) => ({
    getDbRecordsByFormId: builder.query<RecordStructure[], number>({
      queryFn: async (formId) => {
        try {
          const records = (await getRecordsByFormId(formId)) ?? [];
          const normalisedRecords = records
            .filter((record) => record.metadata.state !== 'synced')
            .map((record) => normaliseDbRecord(record, true));
          return { data: normalisedRecords };
        } catch {
          return { error: { status: 'CUSTOM_ERROR', error: 'DB_READ_ERROR' } };
        }
      },
      providesTags: (result) =>
        result
          ? [
              ...result.map((item) => ({ type: 'OfflineRecord', id: item.original.id }) as const),
              { type: 'OfflineRecord', id: 'LIST' },
            ]
          : [{ type: 'OfflineRecord', id: 'LIST' }],
    }),
    getDbRecordById: builder.query<RecordStructure | undefined, number>({
      queryFn: async (recordId) => {
        try {
          const dbRecord = await getRecord(recordId);
          if (dbRecord) {
            return { data: normaliseDbRecord(dbRecord) };
          }
        } catch {
          return { error: { status: 'CUSTOM_ERROR', error: 'DB_READ_ERROR' } };
        }

        return { data: undefined };
      },
      providesTags: (result) => (result ? [{ type: 'OfflineRecord', id: result.original.id }] : []),
    }),
    addDbRecord: builder.mutation<number, DbRecord>({
      queryFn: async ({ record }) => {
        if (!record.id) {
          return { error: { status: 'CUSTOM_ERROR', error: 'DB_WRITE_ERROR' } };
        }

        try {
          await addRecord(record.id, record);
          return { data: record.id };
        } catch {
          return { error: { status: 'CUSTOM_ERROR', error: 'DB_WRITE_ERROR' } };
        }
      },
      invalidatesTags: [{ type: 'OfflineRecord', id: 'LIST' }],
    }),
    updateDbRecord: builder.mutation<number, DbRecord>({
      queryFn: async ({ id, record, syncDetails }) => {
        if (!id || !record.id) {
          return { error: { status: 'CUSTOM_ERROR', error: 'DB_UPDATE_ERROR' } };
        }

        try {
          // fetch the existing patch from the local db
          const dbRecordMutation = await getRecord(id);
          let baseRecord = dbRecordMutation?.baseRecord;
          const dbRecordPatch = dbRecordMutation?.record;
          const dbRecordPatchFields = dbRecordPatch?.fields || {};

          // If no local record exists, and it's a server record, try to get it from offline cache
          if (!baseRecord && !isDbRecordId(record.id)) {
            const dbQueryEntry = await getOfflineRecordQueryData(record.id);

            if (dbQueryEntry) {
              const dbQueryEntryRecordsWrapperData = dbQueryEntry.data as RecordsWrapper;
              baseRecord = dbQueryEntryRecordsWrapperData.result.results[0];
            } else {
              return { error: { status: 'CUSTOM_ERROR', error: 'DB_RECORD_NOT_FOUND' } };
            }
          }

          const updatedPatch = {
            ...dbRecordPatch,
            ...record, // the patch to apply
            fields: { ...dbRecordPatchFields, ...record.fields },
          };

          // update record in the local db
          await updateRecord(id, updatedPatch, baseRecord, dbRecordMutation?.metadata, syncDetails);
          return { data: id };
        } catch {
          return { error: { status: 'CUSTOM_ERROR', error: 'DB_UPDATE_ERROR' } };
        }
      },
      invalidatesTags: (_result, _error, { id }) => [
        { type: 'OfflineRecord', id },
        { type: 'OfflineRecord', id: 'LIST' },
      ],
    }),
    deleteDbRecord: builder.mutation<boolean, number>({
      queryFn: async (id) => {
        if (!id) {
          return { error: { status: 'CUSTOM_ERROR', error: 'DB_DELETE_ERROR' } };
        }

        try {
          await deleteRecord(id);
          return { data: true };
        } catch {
          return { error: { status: 'CUSTOM_ERROR', error: 'DB_DELETE_ERROR' } };
        }
      },
      invalidatesTags: [{ type: 'OfflineRecord', id: 'LIST' }],
    }),
    checkRecordOfflineAvailability: builder.query<boolean, number>({
      queryFn: async (recordId) => {
        try {
          // call needs to match parameter structure in `getRecordById`
          const cached = await getOfflineRecordQueryData(recordId);

          return { data: !!cached };
        } catch {
          return { error: { status: 'CUSTOM_ERROR', error: 'DB_READ_ERROR' } };
        }
      },
    }),
  }),
});

export const {
  useGetDbRecordsByFormIdQuery,
  useGetDbRecordByIdQuery,
  useAddDbRecordMutation,
  useDeleteDbRecordMutation,
  useLazyCheckRecordOfflineAvailabilityQuery,
} = dbApi;

// utility function to get offline record query data
function getOfflineRecordQueryData(recordId: number) {
  return getOfflineQueryData('getRecordById', {
    // call needs to match parameter structure in `getRecordById`
    keyType: 'id',
    fetchLinkedRecords: true,
    url: `/records/simple/${recordId}`,
  });
}

// utility function to normalise a OdinDBRecordMutation into RecordStructure
function normaliseDbRecord(dbRecord: OdinDBRecordMutation, includeDbId = false): RecordStructure {
  const transformed = createRecordData({ ...(dbRecord.baseRecord || {}), ...dbRecord.record });
  return {
    original: {
      ...transformed.original,
      // when displaying local records in the record list, we need the db id to identify the record in the dynamic page
      // otherwise, when opening a record for editing it locally it is not needed as we want the 'patch' to have the server ID
      ...(includeDbId ? { id: dbRecord.id } : {}),
      _offlineMetadata: dbRecord.metadata,
    } as RecordResult,
    flat: {
      ...transformed.flat,
      ...(includeDbId ? { id: dbRecord.id } : {}),
      _offlineMetadata: dbRecord.metadata,
    },
  };
}
